import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { accountDevicesRoute, devicesRoute } from 'src/app/app-routing.module';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { DeviceDetailSaveEvent } from 'src/app/pages/device-details/models/device-detail-form-submit-event';
import { DevicePrivileges } from '../../models/device-privileges';
import { DeviceOrderingOptions, DeviceRegisterOptions } from '../../models/device-register-options';
import { DeviceRegisterListItem } from '../../models/device-register-results.model';
import { DeviceTypeOption } from '../../models/device-type-option.interface';
import { selectOrderingOptions, selectQuickFilters } from '../../state/device-register.reducer';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-system-device-register-view',
    styleUrls: ['./system-device-register-view.component.scss'],
    templateUrl: './system-device-register-view.component.html',
    standalone: false
})
export class SystemDeviceRegisterViewComponent extends EntityRegisterView<
   DeviceRegisterListItem,
   DeviceRegisterOptions,
   DeviceOrderingOptions
> {
   protected selectQuickFilters(): (state: object) => DeviceRegisterOptions | null {
      return selectQuickFilters;
   }

   protected override onQuickFiltersInit(quickFilters: DeviceRegisterOptions): void {
      if (quickFilters) {
         this.selectedDeviceType = quickFilters.typeName;
         this.hideUsedDevices = quickFilters.hideUsedDevices;
         this.showDevicesWithIssues = quickFilters.showDevicesWithIssues;
      }
   }

   @Input() includeInactive: boolean;

   @Input() devicePrivileges: DevicePrivileges;

   @Output() rebootPhone = new EventEmitter<number>();

   @Output() resetPhoneZtp = new EventEmitter<number>();

   @Output() releaseFromExtension = new EventEmitter<DeviceRegisterListItem>();

   @Output() releaseToSystemInventory = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() deleteDevice = new EventEmitter<DeviceDetailSaveEvent>();

   getDynamicRouterLink(device: DeviceRegisterListItem): string {
      if (device.accountName) {
         return `/${accountDevicesRoute}/${device.deviceId}`;
      }
      return device.deviceId !== null ? this.getRouterLink(device.deviceId) : '/devices';
   }

   ngOnInit() {
      super.ngOnInit();
      this.store.pipe(select(selectOrderingOptions), take(1)).subscribe((orderingOptions) => {
         if (orderingOptions) {
            this.orderingOptions = orderingOptions;
         }
      });
   }

   getRouterLink(id: number): string {
      return `/${devicesRoute}/${id}`;
   }

   hideUsedDevices: boolean | undefined = false;

   showDevicesWithIssues: boolean | undefined = false;

   selectedDeviceType: DeviceTypeOption | undefined = undefined;

   @Output() resetHackedAndFraud: EventEmitter<DeviceRegisterListItem>;

   constructor(store: Store, private clipboard: Clipboard, private snackbar: MatSnackBar) {
      super(store);
      this.resetHackedAndFraud = new EventEmitter<DeviceRegisterListItem>();
   }

   toggleHideUsedDevices(event) {
      this.registerOptions = {
         ...this.registerOptions,
         hideUsedDevices: event,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   toggleShowDevicesWithIssues(event) {
      this.registerOptions = {
         ...this.registerOptions,
         showDevicesWithIssues: event,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   resetHackedAndFraudDevice(device: DeviceRegisterListItem) {
      this.resetHackedAndFraud.emit(device);
   }

   onDeviceTypeSelected(deviceType: DeviceTypeOption) {
      this.registerOptions = { ...this.registerOptions, typeName: deviceType };
      this.quickFilterChange.emit(this.registerOptions);
   }

   copyMacAddressToClipboard(macAddress: string) {
      const copied: boolean = this.clipboard.copy(macAddress);

      if (copied) {
         this.snackbar.open('Copied to Clipboard');
      }
   }
   copySipUsernameToClipboard(entity: DeviceRegisterListItem) {
      if (entity && entity.sipUsers && entity.sipUsers[0].userName) {
         const copied: boolean = this.clipboard.copy(entity.sipUsers[0].userName);

         if (copied) {
            this.snackbar.open('Copied to Clipboard');
         } else {
            this.snackbar.open('Failed to Copy');
         }
      }
   }

   copyWebInterfaceUrl(device: DeviceRegisterListItem) {
      const handset = device?.basestationHandsets?.[0];
      const protocol: 'http' | 'https' = handset ? 'http' : 'https';
      let ip = device?.sipUsers?.[0]?.internalIps ?? device?.sipUsers?.[0]?.externalIps;

      if (handset) {
         ip =
            device?.basestationHandsets?.[0].phone.sipUsersConnection.items[0].internalIps ??
            device?.basestationHandsets?.[0].phone.sipUsersConnection.items[0].externalIps;
      }

      if (!ip) return;

      let url = `${protocol}://${ip}`;
      const copied = this.clipboard.copy(url);
      if (copied) {
         this.snackbar?.open('URL copied to clipboard.');
      }
   }

   copyCurrentWebInterfacePassword(device: DeviceRegisterListItem) {
      let password = device?.sipUsers?.[0].phone.adminPassword;

      if (device?.basestationHandsets?.[0]) password = device?.basestationHandsets?.[0].phone.adminPassword;
      if (!password) return;

      const copied = this.clipboard.copy(password);
      if (copied) {
         this.snackbar.open('Password copied to clipboard.');
      }
   }

   rebootPhoneClick(device: DeviceRegisterListItem) {
      if (device.deviceId) {
         this.rebootPhone.emit(device.deviceId);
      }
   }

   canReleaseFromExtension(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canEdit && device.phoneExtension;
   }

   canReleaseToSystemInventory(device: DeviceRegisterListItem) {
      return (
         this.devicePrivileges.canEdit &&
         this.devicePrivileges.canDelete &&
         this.isSystemUser$ &&
         device.accountName != null
      );
   }

   canRemoveFromNDT(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canDelete && this.isSystemUser$;
   }

   ReleaseFromExtensionClick(device: DeviceRegisterListItem) {
      this.releaseFromExtension.emit(device);
   }

   ReleaseToSystemInventoryClick(device: DeviceRegisterListItem) {
      const releaseEventModel: DeviceDetailSaveEvent = {
         device: {
            accountId: null,
            deviceId: device?.deviceId ?? null,
         },
         deviceFields: ['accountId'],
      };

      this.releaseToSystemInventory.emit(releaseEventModel);
   }

   DeleteDeviceClick(device: DeviceRegisterListItem) {
      if (device.deviceId) {
         const releaseEventModel: DeviceDetailSaveEvent = {
            device: {
               accountId: null,
               deviceId: device?.deviceId ?? null,
            },
            deviceFields: ['accountId'],
         };

         this.deleteDevice.emit(releaseEventModel);
      }
   }

   resetPhoneZtpClick(device: DeviceRegisterListItem) {
      if (device.deviceId) {
         this.resetPhoneZtp.emit(device.deviceId);
      }
   }
}
